/**
 * Workflow V2 命令 - 高级工作流管理
 */

import { Command } from 'commander';
import { withErrorHandling } from '../utils/error-handler';
import { WorkflowEngineV2 } from '../core/workflow-engine-v2';
import { WorkflowTemplateManager } from '../core/workflow-templates';
import { WorkflowDefinitionV2 } from '../types/workflow-v2';
import { colors } from '../utils/colors';
import { Table } from 'console-table-printer';
import fs from 'fs-extra';
import path from 'path';
import ora from 'ora';
import { logger } from '../utils/logger';

export function createWorkflowV2Command(): Command {
  const command = new Command('flow');
  
  command
    .description('Advanced workflow management with conditions, loops, and parallel execution')
    .addCommand(createFlowListCommand())
    .addCommand(createFlowRunCommand())
    .addCommand(createFlowCreateCommand())
    .addCommand(createFlowTemplatesCommand())
    .addCommand(createFlowStatusCommand())
    .addCommand(createFlowStopCommand())
    .addCommand(createFlowValidateCommand());
  
  return command;
}

/**
 * flow list - 列出所有工作流
 */
function createFlowListCommand(): Command {
  const command = new Command('list');
  
  command
    .description('List all workflows')
    .alias('ls')
    .action(withErrorHandling(async () => {
      const workflowDir = path.join(process.cwd(), '.workflow', 'flows');
      
      if (!await fs.pathExists(workflowDir)) {
        console.log(colors.warning('No workflows found'));
        return;
      }
      
      const files = await fs.readdir(workflowDir);
      const workflows: WorkflowDefinitionV2[] = [];
      
      for (const file of files) {
        if (file.endsWith('.json')) {
          try {
            const workflow = await fs.readJSON(path.join(workflowDir, file));
            workflows.push(workflow);
          } catch (error) {
            logger.error(`Failed to load workflow ${file}:`, error);
          }
        }
      }
      
      if (workflows.length === 0) {
        console.log(colors.warning('No workflows found'));
        return;
      }
      
      console.log(colors.header('\n📋 Workflows\n'));
      
      const table = new Table({
        columns: [
          { name: 'id', title: 'ID', alignment: 'left' },
          { name: 'name', title: 'Name', alignment: 'left' },
          { name: 'triggers', title: 'Triggers', alignment: 'center' },
          { name: 'nodes', title: 'Nodes', alignment: 'center' },
          { name: 'created', title: 'Created', alignment: 'center' }
        ]
      });
      
      workflows.forEach(workflow => {
        table.addRow({
          id: workflow.id.substring(0, 20) + '...',
          name: workflow.name,
          triggers: workflow.triggers.length,
          nodes: workflow.nodes.length,
          created: workflow.metadata?.created 
            ? new Date(workflow.metadata.created).toLocaleDateString()
            : '-'
        });
      });
      
      table.printTable();
    }));
  
  return command;
}

/**
 * flow run - 运行工作流
 */
function createFlowRunCommand(): Command {
  const command = new Command('run');
  
  command
    .description('Run a workflow')
    .argument('<workflow>', 'Workflow ID or file path')
    .option('-i, --inputs <json>', 'Input parameters as JSON')
    .option('--enable-ai', 'Enable AI features', false)
    .option('--watch', 'Watch workflow execution', false)
    .action(withErrorHandling(async (workflowId: string, options) => {
      // 加载工作流
      let workflow: WorkflowDefinitionV2;
      
      if (workflowId.endsWith('.json')) {
        // 从文件加载
        workflow = await fs.readJSON(workflowId);
      } else {
        // 从 ID 加载
        const workflowPath = path.join(process.cwd(), '.workflow', 'flows', `${workflowId}.json`);
        if (!await fs.pathExists(workflowPath)) {
          console.log(colors.error(`Workflow not found: ${workflowId}`));
          return;
        }
        workflow = await fs.readJSON(workflowPath);
      }
      
      // 解析输入参数
      let inputs = {};
      if (options.inputs) {
        try {
          inputs = JSON.parse(options.inputs);
        } catch (error) {
          console.log(colors.error('Invalid JSON input'));
          return;
        }
      }
      
      console.log(colors.header(`\n🚀 Running Workflow: ${workflow.name}\n`));
      
      // 创建引擎
      const engine = new WorkflowEngineV2({
        enableAI: options.enableAI,
        userId: process.env.USER,
        projectId: await getProjectId(),
        enableMonitoring: true,
        enableLogging: true,
      });
      
      // 监听事件
      if (options.watch) {
        engine.on('node:start', ({ node }) => {
          console.log(colors.info(`▶️  Starting: ${node.name}`));
        });
        
        engine.on('node:complete', ({ node, result }) => {
          console.log(colors.success(`✅ Completed: ${node.name}`));
          if (result) {
            console.log(colors.dim(`   Result: ${JSON.stringify(result).substring(0, 100)}...`));
          }
        });
        
        engine.on('node:error', ({ node, error }) => {
          console.log(colors.error(`❌ Failed: ${node.name}`));
          console.log(colors.error(`   Error: ${error.message}`));
        });
      }
      
      const spinner = ora('Executing workflow...').start();
      
      try {
        // 执行工作流
        const result = await engine.execute(workflow, inputs);
        
        spinner.stop();
        
        if (result.status === 'success') {
          console.log(colors.success('\n✅ Workflow completed successfully!'));
        } else {
          console.log(colors.error('\n❌ Workflow failed'));
        }
        
        // 显示结果摘要
        console.log(colors.header('\n📊 Execution Summary:\n'));
        console.log(`Execution ID: ${result.executionId}`);
        console.log(`Status: ${result.status}`);
        console.log(`Duration: ${result.duration}ms`);
        
        if (result.createdTasks && result.createdTasks.length > 0) {
          console.log(`\nCreated ${result.createdTasks.length} tasks`);
        }
        
        if (result.outputs && Object.keys(result.outputs).length > 0) {
          console.log(colors.header('\n📤 Outputs:\n'));
          Object.entries(result.outputs).forEach(([key, value]) => {
            console.log(`${key}: ${JSON.stringify(value)}`);
          });
        }
        
        if (result.errors && result.errors.length > 0) {
          console.log(colors.header('\n❌ Errors:\n'));
          result.errors.forEach(error => {
            console.log(`[${error.nodeId}] ${error.error}`);
          });
        }
        
      } catch (error) {
        spinner.fail('Workflow execution failed');
        throw error;
      }
    }));
  
  return command;
}

/**
 * flow create - 创建新工作流
 */
function createFlowCreateCommand(): Command {
  const command = new Command('create');
  
  command
    .description('Create a new workflow')
    .option('-t, --template <id>', 'Use a template')
    .option('-n, --name <name>', 'Workflow name')
    .option('-d, --description <desc>', 'Workflow description')
    .option('-o, --output <path>', 'Output file path')
    .action(withErrorHandling(async (options) => {
      const templateManager = new WorkflowTemplateManager();
      
      let workflow: WorkflowDefinitionV2;
      
      if (options.template) {
        // 从模板创建
        console.log(colors.info(`Creating workflow from template: ${options.template}`));
        workflow = await templateManager.createWorkflowFromTemplate(options.template);
        
        if (options.name) {
          workflow.name = options.name;
        }
        if (options.description) {
          workflow.description = options.description;
        }
      } else {
        // 创建空白工作流
        workflow = {
          id: `workflow-${Date.now()}`,
          version: '2.0',
          name: options.name || 'New Workflow',
          description: options.description || 'A new workflow',
          triggers: [],
          nodes: [],
          entryNodes: [],
        };
      }
      
      // 保存工作流
      const workflowDir = path.join(process.cwd(), '.workflow', 'flows');
      await fs.ensureDir(workflowDir);
      
      const outputPath = options.output || path.join(workflowDir, `${workflow.id}.json`);
      await fs.writeJSON(outputPath, workflow, { spaces: 2 });
      
      console.log(colors.success(`\n✅ Workflow created: ${outputPath}`));
      console.log(colors.info(`\nWorkflow ID: ${workflow.id}`));
      console.log(colors.info(`Name: ${workflow.name}`));
      
      console.log(colors.header('\n📌 Next steps:\n'));
      console.log(`1. Edit the workflow: ${colors.command(`code ${outputPath}`)}`);
      console.log(`2. Validate: ${colors.command(`workflow flow validate ${workflow.id}`)}`);
      console.log(`3. Run: ${colors.command(`workflow flow run ${workflow.id}`)}`);
    }));
  
  return command;
}

/**
 * flow templates - 管理工作流模板
 */
function createFlowTemplatesCommand(): Command {
  const command = new Command('templates');
  
  command
    .description('Manage workflow templates')
    .alias('tpl')
    .action(withErrorHandling(async () => {
      const templateManager = new WorkflowTemplateManager();
      const templates = await templateManager.getAllTemplates();
      
      if (templates.length === 0) {
        console.log(colors.warning('No templates found'));
        return;
      }
      
      console.log(colors.header('\n📚 Workflow Templates\n'));
      
      const table = new Table({
        columns: [
          { name: 'id', title: 'ID', alignment: 'left' },
          { name: 'name', title: 'Name', alignment: 'left' },
          { name: 'category', title: 'Category', alignment: 'center', colored: true },
          { name: 'description', title: 'Description', alignment: 'left', maxLen: 40 },
          { name: 'tags', title: 'Tags', alignment: 'left' }
        ]
      });
      
      templates.forEach(template => {
        table.addRow({
          id: template.id,
          name: template.name,
          category: template.category.toUpperCase(),
          description: template.description.length > 40 
            ? template.description.substring(0, 37) + '...' 
            : template.description,
          tags: template.tags.join(', ')
        });
      });
      
      table.printTable();
      
      console.log(colors.header('\n💡 Usage:\n'));
      console.log(`• Create from template: ${colors.command('workflow flow create -t <template-id>')}`);
      console.log(`• View template details: ${colors.command('workflow flow templates <template-id>')}`);
    }));
  
  // 子命令：查看模板详情
  command
    .command('show <id>')
    .description('Show template details')
    .action(withErrorHandling(async (templateId: string) => {
      const templateManager = new WorkflowTemplateManager();
      const template = await templateManager.getTemplate(templateId);
      
      if (!template) {
        console.log(colors.error(`Template not found: ${templateId}`));
        return;
      }
      
      console.log(colors.header(`\n📚 Template: ${template.name}\n`));
      console.log(`ID: ${template.id}`);
      console.log(`Category: ${template.category}`);
      console.log(`Description: ${template.description}`);
      console.log(`Tags: ${template.tags.join(', ')}`);
      console.log(`Author: ${template.metadata.author || 'Unknown'}`);
      console.log(`Version: ${template.metadata.version || '1.0.0'}`);
      
      if (template.variables && template.variables.length > 0) {
        console.log(colors.header('\n📝 Variables:\n'));
        template.variables.forEach(variable => {
          console.log(`• ${variable.name} (${variable.type}) - ${variable.description}`);
          if (variable.default !== undefined) {
            console.log(`  Default: ${variable.default}`);
          }
        });
      }
      
      if (template.examples && template.examples.length > 0) {
        console.log(colors.header('\n💡 Examples:\n'));
        template.examples.forEach((example, index) => {
          console.log(`${index + 1}. ${example.name}`);
          console.log(`   ${example.description}`);
          console.log(`   Inputs: ${JSON.stringify(example.inputs)}`);
        });
      }
      
      console.log(colors.header('\n🚀 Create workflow from this template:\n'));
      console.log(colors.command(`workflow flow create -t ${templateId} -n "My Workflow"`));
    }));
  
  return command;
}

/**
 * flow status - 查看运行中的工作流状态
 */
function createFlowStatusCommand(): Command {
  const command = new Command('status');
  
  command
    .description('Show running workflows status')
    .action(withErrorHandling(async () => {
      // TODO: 实现工作流状态查看
      console.log(colors.info('Workflow status monitoring not implemented yet'));
    }));
  
  return command;
}

/**
 * flow stop - 停止运行中的工作流
 */
function createFlowStopCommand(): Command {
  const command = new Command('stop');
  
  command
    .description('Stop a running workflow')
    .argument('<executionId>', 'Execution ID')
    .action(withErrorHandling(async (executionId: string) => {
      // TODO: 实现停止工作流
      console.log(colors.info('Workflow stopping not implemented yet'));
    }));
  
  return command;
}

/**
 * flow validate - 验证工作流定义
 */
function createFlowValidateCommand(): Command {
  const command = new Command('validate');
  
  command
    .description('Validate workflow definition')
    .argument('<workflow>', 'Workflow ID or file path')
    .action(withErrorHandling(async (workflowId: string) => {
      // 加载工作流
      let workflow: WorkflowDefinitionV2;
      
      try {
        if (workflowId.endsWith('.json')) {
          workflow = await fs.readJSON(workflowId);
        } else {
          const workflowPath = path.join(process.cwd(), '.workflow', 'flows', `${workflowId}.json`);
          workflow = await fs.readJSON(workflowPath);
        }
      } catch (error) {
        console.log(colors.error(`Failed to load workflow: ${error.message}`));
        return;
      }
      
      console.log(colors.header(`\n🔍 Validating Workflow: ${workflow.name}\n`));
      
      const errors: string[] = [];
      const warnings: string[] = [];
      
      // 验证基本结构
      if (!workflow.id) errors.push('Missing workflow ID');
      if (!workflow.version) errors.push('Missing version');
      if (!workflow.name) errors.push('Missing name');
      if (!workflow.nodes || workflow.nodes.length === 0) errors.push('No nodes defined');
      if (!workflow.entryNodes || workflow.entryNodes.length === 0) errors.push('No entry nodes defined');
      
      // 验证节点
      const nodeIds = new Set(workflow.nodes.map(n => n.id));
      
      workflow.entryNodes.forEach(nodeId => {
        if (!nodeIds.has(nodeId)) {
          errors.push(`Entry node not found: ${nodeId}`);
        }
      });
      
      workflow.nodes.forEach(node => {
        // 验证节点引用
        const checkNodeRefs = (refs?: string[]) => {
          refs?.forEach(ref => {
            if (!nodeIds.has(ref)) {
              errors.push(`Node ${node.id} references unknown node: ${ref}`);
            }
          });
        };
        
        checkNodeRefs(node.onSuccess);
        checkNodeRefs(node.onFailure);
        checkNodeRefs(node.onComplete);
        
        if (node.branches) {
          checkNodeRefs(node.branches.true);
          checkNodeRefs(node.branches.false);
        }
        
        if (node.loopBody) {
          checkNodeRefs(node.loopBody);
        }
        
        if (node.parallel?.branches) {
          node.parallel.branches.forEach(branch => checkNodeRefs(branch));
        }
        
        // 验证节点特定配置
        switch (node.type) {
          case 'task':
            if (!node.task) errors.push(`Task node ${node.id} missing task definition`);
            break;
          case 'condition':
            if (!node.condition) errors.push(`Condition node ${node.id} missing condition`);
            break;
          case 'loop':
            if (!node.loop) errors.push(`Loop node ${node.id} missing loop config`);
            if (!node.loopBody) errors.push(`Loop node ${node.id} missing loop body`);
            break;
          case 'parallel':
            if (!node.parallel) errors.push(`Parallel node ${node.id} missing parallel config`);
            break;
          case 'action':
            if (!node.action) errors.push(`Action node ${node.id} missing action config`);
            break;
        }
      });
      
      // 检查循环依赖
      // TODO: 实现循环依赖检查
      
      // 显示结果
      if (errors.length === 0) {
        console.log(colors.success('✅ Workflow is valid!'));
      } else {
        console.log(colors.error(`❌ Found ${errors.length} errors:`));
        errors.forEach(error => {
          console.log(colors.error(`   • ${error}`));
        });
      }
      
      if (warnings.length > 0) {
        console.log(colors.warning(`\n⚠️  ${warnings.length} warnings:`));
        warnings.forEach(warning => {
          console.log(colors.warning(`   • ${warning}`));
        });
      }
    }));
  
  return command;
}

/**
 * 获取项目 ID
 */
async function getProjectId(): Promise<string | undefined> {
  const configPath = path.join(process.cwd(), '.workflow', 'config', 'project.json');
  if (await fs.pathExists(configPath)) {
    const config = await fs.readJSON(configPath);
    return config.id;
  }
  return undefined;
}